//components/Share/ShareForm.jsx
//juy29 Shareform with logic working but needs UI fix for scrollabel lists:
//jul28 ShareForm with commenter permissions still there
//july28 shareform without confirm button:
//july28 ShareForm without patch to dialog showing owner as current user:
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { TextField, List, ListItem, Typography, FormControl, Select, MenuItem, InputLabel, Box, Grid, Button, Avatar, Snackbar, CircularProgress } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { setFormSharingSettings, getFormById } from '../../utils/firestore';
import { useFormContext } from '../../contexts/FormContext'; // Import FormContext to get form details
import { useAuth } from '../../contexts/AuthContext'; // Import AuthContext to get current user and permissions
import { useUserManagement } from '../../contexts/UserManagementContext'; // Import UserManagementContext to fetch users

const ShareForm = ({
  formName:     propFormName,
  formUrl:      propFormUrl,
  formId:       propFormId,
  formStatus,
  isPublic:     propIsPublic,
  orgAccess:    propOrgAccess,
  sharedWith:   propSharedWith
}) => {
//  Get current user and permission utilities
const { currentUser, hasPermission, organizationId } = useAuth();

//  Get formId & formDetails from FormContext
const { formId: contextFormId, formDetails, setFormDetails } = useFormContext() || {};
const formId   = propFormId   || contextFormId;
const formName = propFormName || formDetails?.title || '';
const formUrl  = propFormUrl  || (formStatus === 'published' ? `/forms/${formId}` : null);

/* ──────────────────────────────────────────────────────────────
   EFFECTIVE ROLE (form‑level) overrides global role
   ─────────────────────────────────────────────────────────── */
const effectiveRole = useMemo(() => {
  if (!currentUser) return 'none';

  /* 1️Owner */
  if (formDetails?.owner === currentUser.uid) return 'owner';

  /* 2️Explicit ACL on the form */
  if (formDetails?.sharedWith?.[currentUser.uid]) {
    return formDetails.sharedWith[currentUser.uid];   // 'edit' | 'view'
  }

  /* 3️Org‑wide access */
  if (
    formDetails?.organizationId === organizationId &&
    formDetails?.orgAccess === 'edit'
  ) return 'edit';
  if (
    formDetails?.organizationId === organizationId &&
    formDetails?.orgAccess === 'view'
  ) return 'view';

  return 'none';
}, [
  formDetails,              // covers owner, sharedWith, orgAccess, organizationId
  currentUser?.uid,
  organizationId,
]);

/* Derived capability flags */
const isOwner             = effectiveRole === 'owner';
const canEditForm         = effectiveRole === 'edit' || isOwner;
const canManagePeople     = isOwner || (canEditForm && hasPermission('shareForm'));
const canChangeVisibility = isOwner;          // owner‑only
const canShareForm        = canManagePeople;  // legacy alias


  // 📦 Get user management context data
//  const userManagement = useUserManagement();
//  const users = userManagement?.users ?? [];
//  const invitations = userManagement?.invitations ?? [];
//  const fetchUsersAndInvitations = userManagement?.fetchUsersAndInvitations ?? (() => {});

// 📦 Get user‑management context data  (now includes rosterReady)
const {
  users        = [],
  invitations  = [],
  rosterReady, 
  fetchUsersAndInvitations,
} = useUserManagement() ?? {};

  const initialOrgAccess  = propOrgAccess  ?? formDetails?.orgAccess  ?? 'none';
  const initialIsPublic   = propIsPublic   ?? formDetails?.isPublic   ?? false;

  /** helper → convert (isPublic, orgAccess) → dropdown */
  const deriveInitialGeneralAccess = () => {
    if (initialIsPublic)        return 'anyone';     // public link
    if (initialOrgAccess !== 'none') return 'org-wide';
    return 'restricted';
  };

  // 🧠 Local state
  const [email, setEmail] = useState('');
  const [generalAccessLevel,  setGeneralAccessLevel]  = useState(
    deriveInitialGeneralAccess()
  );
  const [orgAccess, setOrgAccess] = useState(initialOrgAccess);
//  const [sharedWith, setSharedWith] = useState(initialSharedWith);
 
  const [emailError, setEmailError] = useState(null); // 'invalid' | 'unauthorized' | null
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // 🛑 Ensure users aren't fetched repeatedly
  const hasFetchedUsersRef = useRef(false);

/* ──────────────────────────────────────────────────────────────
   helper → human‑readable menu text                            
   ─────────────────────────────────────────────────────────── */
const labelForLevel = (lv) =>
  lv === 'Remove' ? 'Remove Access' : lv;

/* ──────────────────────────────────────────────────────────────
   helper ➜ map app‑role / permissions → allowed form‑levels
   (memoised so its reference stays stable)                      ◀─ NEW
   ─────────────────────────────────────────────────────────── */
const allowedLevelsForRole = useCallback(
  (role = 'viewer', perms = []) => {
    /* 1️⃣  Fine‑grained permission flags win */
    const canEditByPerms =
      perms.includes('editForms') || perms.includes('shareForm');

    if (canEditByPerms) return ['Viewer', 'Editor', 'Remove'];

    /* 2️⃣  Fallback when perms array is missing (legacy accounts) */
    const fallbackEditorRoles = [
      'editor',
      'admin',
      'owner',
      'superAppadmin',
      'appSupport',
      'billingManager',
    ];

    return fallbackEditorRoles.includes(role)
      ? ['Viewer', 'Editor', 'Remove']
      : ['Viewer', 'Remove'];
  },
  [] // ⬅️  empty deps → memoised once
);


// ── helper: derive first list (owner + existing collaborators)
const buildInitialPeople = (users, sharedWith, ownerId) => {
  if (!users.length || !sharedWith) return [];
  const list = Object.entries(sharedWith).flatMap(([uid, lvl]) => {
    const m = users.find(u => u.id === uid);
    return m
      ? [{
          uid,
          email: m.email,
          access: lvl === 'edit'     ? 'Editor'
                :                     'Viewer',
          firstName: m.firstName || '',
          lastName : m.lastName  || '',
          role: m.role || 'viewer',                     
          permissions: m.permissions || [],

        }]
      : [];
  });
  const owner = users.find(u => u.id === ownerId);
  if (owner) list.unshift({               // owner always first
    uid: owner.id,
    email: owner.email,
    access: 'Owner',
    firstName: owner.firstName || '',
    lastName : owner.lastName  || '',
    role: owner.role || 'owner',
    permissions: owner.permissions || [],   
  });
  return list;
};

const [peopleWithAccess, setPeopleWithAccess] = useState(() =>
  buildInitialPeople(users, formDetails?.sharedWith, formDetails?.owner)
);

 /* ---------------------------------------------------------------------- */
  /*                 🔹 helper to PERSIST sharing updates                    */
  /* ---------------------------------------------------------------------- */
  const persistSharing = useCallback(
    async (updates /* {isPublic?,orgAccess?,sharedWith?} */) => {
      if (!formId) return;

      // [LOG‑1] open a collapsible group so the console isn’t noisy
//      console.groupCollapsed('[ShareForm] persistSharing');
//      console.log('incoming updates  ➜', updates);
//      console.log('canManagePeople   ➜', canManagePeople);
//      console.log('canChangeVisibl.  ➜', canChangeVisibility);

      /* ── owner‑only fields (visibility) ───────────────────────── */
      if (
        !canChangeVisibility &&
        (updates.isPublic !== undefined || updates.orgAccess !== undefined)
      ) {
        console.warn('[ShareForm] Blocked visibility change by non‑owner.');
        console.groupEnd();
        return;
      }

      /* ── collaborator list needs shareForm permission ─────────── */
      if (updates.sharedWith !== undefined && !canManagePeople) {
        console.warn('[ShareForm] Blocked collaborator change — no rights.');
        console.groupEnd();
        return;
      }

      /*   mirror array field so Firestore can query it */
      const payload = { ...updates };                  //  clone updates
      if (payload.sharedWith) {                        //  when list changes
        payload.sharedWithIds = Object.keys(payload.sharedWith); 
      }

      try {
        // [LOG‑P1] ---- PERSIST START ----
        console.groupCollapsed('[ShareForm] persistSharing');
//        console.log('payload ➜', payload);             //  log enriched payload
        console.time('[ShareForm] firestore‑roundtrip');

        // [LOG‑2] optimistic state update
//        console.log('[ShareForm] writing to FormContext (optimistic)');

        // ①  optimistic update so the UI can’t “snap back” if the
        //    round‑trip to Firestore is slow
        setFormDetails(prev => ({ ...prev, ...payload }));   //  payload
        // ②  real network write
        console.time('[ShareForm] Firestore round‑trip');

        await setFormSharingSettings(formId, payload);       //  use payload
        console.timeEnd('[ShareForm] Firestore round‑trip');
        console.info('[ShareForm] ✅ Firestore write OK');

      } catch (err) {
        console.error('[ShareForm] ❌ Failed to persist sharing settings:', err);
        alert(`Could not save sharing settings: ${err.message}`);
      } finally {
        console.groupEnd();  // [LOG‑3] always close the group
      }
    },
    [formId, canManagePeople, canChangeVisibility, setFormDetails]
  );


  /* ---------------------------------------------------------------------- */
  /*                         sanity checks / side-effects                   */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (!formId) {
      console.error('[ShareForm] formId is undefined. Sharing may not work properly.');
    }
  }, [formId]);

  // 📥 Load users + invitations when component mounts
  useEffect(() => {
    const fetchData = async () => {
    if (
      !hasFetchedUsersRef.current &&
      canShareForm &&
      users.length === 0 &&
      fetchUsersAndInvitations
    ) {

//        console.log('[ShareForm] Fetching organization users and invitations...');
        try {
          await fetchUsersAndInvitations();
          hasFetchedUsersRef.current = true;
        } catch (error) {
          console.error('[ShareForm] Failed to fetch users:', error);
        }
      }
    };

    fetchData();
  }, [fetchUsersAndInvitations, users.length, canShareForm]);

  //  Email validation helper
  const isValidEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email.trim());
  };

  //  Build list of allowed invitees (users and pending invitations)
  const allowedInvitees = useMemo(() => {
    const invitedEmails = new Set();
    const entries = [];

    users.forEach((user) => {
      if (!invitedEmails.has(user.email)) {
        invitedEmails.add(user.email);
        entries.push({ 
          email: user.email, 
          firstName: user.firstName, 
          lastName: user.lastName, 
          role: user.role || 'Member' 
        });
      }
    });

    invitations.forEach((inv) => {
      if (!invitedEmails.has(inv.email)) {
        invitedEmails.add(inv.email);
        entries.push({ 
          email: inv.email, 
          firstName: '', 
          lastName: '', 
          role: 'Invited' 
        });
      }
    });

    return entries;
  }, [users, invitations]);


const getUidByEmail = (email) => {
  const hit = users.find(u => u.email === email);
  return hit ? hit.id : null;          // uid string or null
};

  //  Name initials helper
  const getNameInitials = useCallback((firstName = '', lastName = '') => {
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial ? ` ${lastInitial}` : ''}`;
  }, []);

  //  Fetch user info by email from allowedInvitees
  const fetchUserProfileByEmail = useCallback(
    (email) => allowedInvitees.find((entry) => entry.email === email),
    [allowedInvitees]
  );



// STEP 1: keep dropdown in sync **only when** we are inside FormBuilder
//         (i.e. props didn’t already carry the flags)
useEffect(() => {
  if (propIsPublic !== undefined || propOrgAccess !== undefined) return;
  if (formDetails?.isPublic === undefined) return;   // still null – ignore

//  console.log('[ShareForm] (FormBuilder) syncing dropdown from FormContext');

  /* ──────────────────────────────────────────────────────────────
     1  Update the dropdown that the user actually sees          */
  const newGeneral =
    formDetails.isPublic
      ? 'anyone'
      : formDetails.orgAccess && formDetails.orgAccess !== 'none'
      ? 'org-wide'
      : 'restricted';

  setGeneralAccessLevel(newGeneral);

  /* ──────────────────────────────────────────────────────────────
     2  ALSO update the hidden orgAccess ref so that if the user
         switches from “Org-wide” → “Restricted”, we know which
         value to flip back to.  This does **not** touch Firestore. */
  setOrgAccess(formDetails.orgAccess || 'none');
}, [
  propIsPublic,
  propOrgAccess,
  formDetails?.isPublic,
  formDetails?.orgAccess      // already in deps, nothing else changes
]);

// STEP 2: Handler for changes to the "General Access" dropdown
/* ---------------------------------------------------------------------- */
/*        Handler: “General Access” dropdown  →  isPublic / orgAccess     */
/* ---------------------------------------------------------------------- */
const handleGeneralAccessChange = useCallback(
  async (event) => {
    const newVal = event.target.value;                 // 'restricted' | 'org-wide' | 'anyone'
//    console.log('[ShareForm] User changed General Access to:', newVal);

    // update local UI state immediately
    setGeneralAccessLevel(newVal);

    try {
      if (newVal === 'anyone') {
        /*  Public link: everyone can view
            – Firestore:  { isPublic:true,  orgAccess:'none' }
            – Local ctx :  same                                               */
        setOrgAccess('none');
        await persistSharing({ isPublic: true, orgAccess: 'none' });
 //       console.log('[ShareForm] isPublic=true, orgAccess=none persisted');
      } else if (newVal === 'org-wide') {
        /*  Visible to the whole org
            – Firestore:  { isPublic:false, orgAccess:'view' }  */
        setOrgAccess('view');
        await persistSharing({ isPublic: false, orgAccess: 'view' });
 //       console.log('[ShareForm] isPublic=false, orgAccess=view persisted');
      } else {
        /*  Restricted – only explicitly-shared people  */
        setOrgAccess('none');
        await persistSharing({ isPublic: false, orgAccess: 'none' });
//        console.log('[ShareForm] isPublic=false, orgAccess=none persisted');
      }
    } catch (err) {
      console.error('[ShareForm] Failed updating General Access in Firestore:', err);
    }
  },
  [persistSharing, setOrgAccess]   // deps: persistSharing already captures formId & ctx
);



  const handleEmailChange = useCallback((event) => {
    const inputValue = event.target.value;
    setEmail(inputValue);

    //  Only filter suggestions if user typed more than 2 characters
    if (inputValue.length > 2) {
      const filteredSuggestions = allowedInvitees.filter(
        (entry) =>
          entry.email.toLowerCase().includes(inputValue.toLowerCase()) &&
          !peopleWithAccess.some((p) => p.email === entry.email)
      );

      setEmailSuggestions(filteredSuggestions);
      setShowSuggestions(filteredSuggestions.length > 0);
    } else {
      setEmailSuggestions([]);
      setShowSuggestions(false);
    }
  }, [allowedInvitees, peopleWithAccess]);

  const handleAddPerson = useCallback(() => {
//    console.log('[ShareForm] Adding person:', email);

    const emailList = email
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean); // Filter out empty entries

    let hasInvalidFormat = false;
    let hasUnauthorizedEmail = false;
    const newPeopleWithAccess = [];

    emailList.forEach((emailItem) => {
      if (isValidEmail(emailItem)) {
        const alreadyExists = peopleWithAccess.some((person) => person.email === emailItem);

        if (alreadyExists) {
          console.warn(`[ShareForm] Skipping duplicate entry: ${emailItem}`);
          return;
        }

        const userProfile = fetchUserProfileByEmail(emailItem);

        if (!userProfile) {
          console.warn(`[ShareForm] Email not found in organization invite list: ${emailItem}`);
          hasUnauthorizedEmail = true;
          return;
        }

        newPeopleWithAccess.push({
          email: emailItem,
          uid  : getUidByEmail(emailItem) || null,
          access: 'Viewer',
          firstName: userProfile.firstName || '',
          lastName: userProfile.lastName || '',
        });
      } else {
        hasInvalidFormat = true;
      }
    });

    if (hasInvalidFormat) {
      console.warn('[ShareForm] Invalid email format detected. Not all emails were added.');
      setEmailError('invalid');
    } else if (hasUnauthorizedEmail) {
      console.warn('[ShareForm] Unauthorized email detected (not in organization).');
      setEmailError('unauthorized');
    } else {
      setPeopleWithAccess((prevPeople) => [...prevPeople, ...newPeopleWithAccess]);
      setEmailError(null);
      setEmail('');
    }
  }, [email, peopleWithAccess, fetchUserProfileByEmail]);


  const handleSuggestionSelect = useCallback((selectedEmail) => {
    const alreadyExists = peopleWithAccess.some((person) => person.email === selectedEmail);
    if (alreadyExists) {
      console.warn(`[ShareForm] User already added: ${selectedEmail}`);
      return;
    }

    const userProfile = fetchUserProfileByEmail(selectedEmail);

    if (!userProfile) {
      console.warn(`[ShareForm] Selected suggestion not in organization list: ${selectedEmail}`);
      return;
    }

    setPeopleWithAccess(prev => [
      ...prev,
      {
        email : selectedEmail,
        uid   : getUidByEmail(selectedEmail) || null,
        access: 'Viewer',
        firstName: userProfile.firstName || '',
        lastName : userProfile.lastName  || '',
      },
    ]);


    setEmail('');
    setShowSuggestions(false);
  }, [fetchUserProfileByEmail, peopleWithAccess]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      if (emailSuggestions.length > 0) {
        handleSuggestionSelect(emailSuggestions[0].email);
      } else {
        handleAddPerson();
      }
    }
  }, [emailSuggestions, handleAddPerson, handleSuggestionSelect]);
/* ---------------------------------------------------------------------- */
/*   Handle dropdown changes — now blocks illegal upgrades                */
/* ---------------------------------------------------------------------- */
const handleAccessChange = useCallback((email, newAccess) => {
  setPeopleWithAccess(prevPeople =>
    prevPeople
      .map(person => {
        if (person.email !== email) return person;   // untouched rows

        /* SELF‑REMOVAL GUARD
           Editors can’t kick themselves out unless they
           first downgrade to Viewer.  Viewers may remove themselves. */
        if (
          person.uid === currentUser.uid &&          // it’s *me*
          person.access !== 'Viewer' &&              // I’m more than a viewer
          newAccess === 'Remove'                     // trying to leave
        ) {
         alert(`You can’t remove yourself while you’re an Editor or Owner.
        Downgrade to Viewer first, or ask another editor to remove you.`);
          return person;
        }


        /* prevent granting levels beyond invitee’s app role */
        const allowed = allowedLevelsForRole(person.role, person.permissions);
        if (!allowed.includes(newAccess)) {
          console.warn(
            `[ShareForm] ❌ Attempt to grant "${newAccess}" to ${email} ` +
            `(role=${person.role}). Allowed: ${allowed.join(', ')}`
          );
          return person;                             // leave unchanged
        }

        /* ↺ Existing logic (remove vs. update) */
        if (newAccess === 'Remove') {
//          console.log(`[ShareForm] Removing access for ${email}`);
          return null;                               // mark for deletion
        }

        console.log(`[ShareForm] Updating access for ${email} to ${newAccess}`);
        return { ...person, access: newAccess };
      })
      .filter(Boolean)                               // drop any null rows
  );
}, [allowedLevelsForRole, currentUser?.uid]);

/* ------------------------------------------------------------------ */
/*   Re‑sync people list whenever roster / ACL changes             */
/*   (now also converts legacy "Commenter" ➜ "Viewer" rows)           */
/* ------------------------------------------------------------------ */
useEffect(() => {
  const initial = buildInitialPeople(
    users,
    formDetails?.sharedWith,
    formDetails?.owner
  );

  // 🧹 one‑time migration logic
  const cleaned = initial.map(p =>
    p.access === 'Commenter' ? { ...p, access: 'Viewer' } : p
  );

  setPeopleWithAccess(cleaned);
}, [users, formDetails?.sharedWith, formDetails?.owner]);


/* ---------------------------------------------------------------------- */
/*   2.  Persist people-changes → Firestore whenever list mutates        */
/*      (debounced 500 ms to avoid spamming on every keystroke)            */
/* ---------------------------------------------------------------------- */
const lastMapRef   = useRef(null);
const persistTimer = useRef(null);

useEffect(() => {
  // ── bail out early if we have nothing useful to write ─────────────── 
  if (!formId || !canShareForm || peopleWithAccess.length === 0) {
//    console.log('[ShareForm] ⏩ persist-effect skipped (no form / no rights)');
    return;
  }


  // build { uid : level }
  const map = {};
  peopleWithAccess.forEach(p => {
    if (p.access === 'Owner') return;        // never write the owner

    // 🔸 NEW – skip rows that still have no UID (invitees who haven't accepted)
    if (!p.uid) return;

    const lvl = p.access === 'Editor' ? 'edit' : 'view';

    map[p.uid] = lvl;                        // always a defined key now

  });

  //  nothing changed?  bail out
  if (lastMapRef.current &&
      JSON.stringify(lastMapRef.current) === JSON.stringify(map)) {
    return;
  }
  lastMapRef.current = map;

//  console.log('[ShareForm] 🔁 persist-effect fired');
//  console.log('[ShareForm] peopleWithAccess ➜', peopleWithAccess);

  clearTimeout(persistTimer.current);
  persistTimer.current = setTimeout(() => {
//    console.log('[ShareForm] 🕑 debounce elapsed – calling persistSharing()');
    persistSharing({ sharedWith: map });
  }, 500);

  return () => clearTimeout(persistTimer.current);
}, [peopleWithAccess, formId, canShareForm, persistSharing]);


/* ---------------------------------------------------------------------- */


// Copy link helper (default to canonical, but supports vanity)
const handleCopyLink = useCallback((urlOverride) => {
  const link =
    typeof urlOverride === 'string' && urlOverride.trim()
      ? urlOverride.trim()
      : `${window.location.origin}/forms/${formId}`;

  console.log('[ShareForm] Copy link:', link);

  navigator.clipboard
    .writeText(link)
    .then(() => setShowCopySuccess(true))
    .catch((err) =>
      console.error('[ShareForm] Failed to copy link:', err)
    );
}, [formId]);

/* ──────────────────────────────────────────────────────────────
   Wait until the org roster is loaded → prevents empty‑list flicker
   ─────────────────────────────────────────────────────────── */
if (!rosterReady && canShareForm) {
  return (
    <Box sx={{ p: 4, width: 400, textAlign: 'center' }}>
      <CircularProgress size={28} />
    </Box>
  );
}

/* ──────────────────────────────────────────────────────────────
   Share link URLs (prefer vanity when present)
   - vanitySlug lives on the form doc (formDetails.vanitySlug)
   - canonical stays the SPA route (always valid)
   - displayLink prefers vanity when present
   ─────────────────────────────────────────────────────────── */
const vanitySlug = String(formDetails?.vanitySlug || '')
  .trim()
  .toLowerCase();

const canonicalLink = `${window.location.origin}/forms/${formId}`;

const vanityLink =
  vanitySlug ? `https://${vanitySlug}.utmsmartmanager.com` : '';

const displayLink = vanityLink || canonicalLink;

return (
<Box
  sx={{
    mt: 0,
    maxHeight: '80vh',          // cap dialog height
    overflow: 'hidden',         // stop the dialog itself from scrolling
    display: 'flex',
    flexDirection: 'column',
  }}
>
    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
      Share: {formName}
    </Typography>

    {/* Add people input */}
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        margin="dense"
        label="Add people"
        type="email"
        value={email}
        onChange={handleEmailChange}
        onKeyPress={handleKeyPress}
        variant="outlined"
        placeholder="Enter email address"
        error={!!emailError}
        helperText={
          !canShareForm
            ? "You do not have permission to share this form."
            : emailError === 'invalid'
            ? "Please enter a valid email address."
            : emailError === 'unauthorized'
            ? "This user is not part of your organization. Invite them first."
            : ""
        }
        disabled={!canShareForm}
      />

      {/* Email suggestions dropdown */}
      {showSuggestions && emailSuggestions.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            zIndex: 10,
            background: 'white',
            border: '1px solid #ccc',
            width: '100%',
            maxWidth: '100%',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          {emailSuggestions.map((suggestion, index) => (
            <ListItem
              button
              key={index}
              onClick={() => handleSuggestionSelect(suggestion.email)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                px: 2,
                py: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              {/* Full name (larger text) */}
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: 'text.primary', lineHeight: 1.2 }}
              >
                {suggestion.firstName} {suggestion.lastName}
              </Typography>

              {/* Email (smaller + muted) */}
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', lineHeight: 1 }}
              >
                {suggestion.email}
              </Typography>
            </ListItem>
          ))}
        </Box>
      )}
    </Box>

    {/* People with Access */}
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
      People with Access
    </Typography>

    {peopleWithAccess.length > 0 ? (
    <List dense sx={{ maxHeight: '250px', overflowY: 'auto' }}>
    {peopleWithAccess.map((person, index) => {
      /* figure out what levels this person can get */
      const levels = allowedLevelsForRole(person.role, person.permissions);
    /*  true when there is only one possible level */
    const single = levels.length === 1;

    return (
      <ListItem
        key={index}
        sx={{
          paddingBottom: '8px',
          paddingTop: '8px',
          '&:hover': { backgroundColor: '#f0f0f0', cursor: 'pointer' },
        }}
      >
            <Grid container spacing={1} alignItems="center" wrap="nowrap">
              <Grid item>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.875rem',
                  }}
                >
                  {getNameInitials(person.firstName, person.lastName)}
                </Avatar>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {person.firstName} {person.lastName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1 }}
                  >
                    {person.email}
                  </Typography>
                </Box>
              </Grid>
            <Grid item>
              {person.access === 'Owner' ? (
                <Typography variant="body2" color="text.disabled">Owner</Typography>
              ) : single ? (
                /* ─ only one possible level: render text instead of a Select ─ */
                <Typography variant="body2">
                  {labelForLevel(levels[0])}
                </Typography>
              ) : (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={person.access}
                    onChange={(e) => handleAccessChange(person.email, e.target.value)}
                    disabled={!canShareForm}
                  >
                    {levels.map((lv) => (
                      <MenuItem key={lv} value={lv}>
                        <Typography
                          variant="body2"
                          color={lv === 'Remove' ? 'error' : 'text.primary'}
                        >
                          {labelForLevel(lv)}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

        </Grid>
      </ListItem>
    );
  })}
</List>

    ) : (
  <Typography
    variant="body2"
    sx={{ mt: 1 }}
    color="textSecondary"
  >
    {canShareForm
      ? 'No collaborators have been added yet.'
      : 'You do not have permission to share this form. Contact the owner.'}
  </Typography>

    )}

    {/* General Access */}
      <FormControl fullWidth margin="normal" disabled={!canChangeVisibility}>
      <InputLabel>General Access</InputLabel>
      <Select
        value={generalAccessLevel}
        onChange={handleGeneralAccessChange}
        label="General Access"
      >
        <MenuItem value="restricted">Restricted</MenuItem>
        <MenuItem value="org-wide">Organization-wide</MenuItem>
        <MenuItem value="anyone">Anyone with the link</MenuItem>
      </Select>
    </FormControl>

{/* Form URL row (minimal): outlined copy icon + clickable URL */}
<Box sx={{ mt: 2, width: '100%' }}>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Tooltip title="Copy link">
      <span>
        <IconButton
          onClick={() => handleCopyLink(displayLink)}
          disabled={formStatus !== 'published' || !canShareForm}
          size="small"
          sx={{
            ml: -0.5,
            color: 'primary.main',
            borderRadius: '8px',
            '&:hover': {
              color: 'primary.dark',
              backgroundColor: 'transparent',
            },
            '&.Mui-disabled': {
              color: 'action.disabled',
            },
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>

<Tooltip
  title={
    formStatus === 'published' && canShareForm
      ? 'Click to copy'
      : ''
  }
>
  <Box
    role="button"
    tabIndex={0}
    onClick={() => handleCopyLink(displayLink)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCopyLink(displayLink);
      }
    }}
    sx={{
      display: 'inline-flex',     // ✅ hover only wraps the URL text area
      minWidth: 0,                // ✅ still allows ellipsis
      cursor:
        formStatus === 'published' && canShareForm
          ? 'pointer'
          : 'default',
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: 2,
        borderRadius: 1,
      },
    }}
  >
    <Typography
      variant="body2"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        // ✅ only color changes on hover (no background)
        color:
          formStatus === 'published'
            ? 'text.primary'
            : 'text.disabled',
        '&:hover': {
          color:
            formStatus === 'published' && canShareForm
              ? 'primary.main'
              : undefined,
        },
      }}
      title={displayLink}
    >
      {displayLink}
    </Typography>
  </Box>
</Tooltip>

  </Box>
</Box>





    <Snackbar
      open={showCopySuccess}
      autoHideDuration={3000}
      onClose={() => setShowCopySuccess(false)}
      message="URL copied to clipboard"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />

  </Box>
);
};

export default ShareForm;
